import {MatDialog} from '@angular/material/dialog';
import {
    PreviewImageDialogComponent
} from '@emm/common-items/dialogs/preview-image-dialog/preview-image-dialog.component';
import {MatConfirmDialogComponent} from '@emm/shared/dialogs/mat-confirm-dialog/mat-confirm-dialog.component';
import {ActivatedRoute, Router} from '@angular/router';
import {ItemDataService} from '../../services/item-data.service';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {BaseItemRequestPayload, Item} from '../../models/item.model';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs';
import {ItemDetailStoreService} from '@emm/menu/store/item-detail/item-detail-store.service';
import {catchError, filter, map, skip, switchMap, takeUntil, tap} from 'rxjs/operators';
import {ItemAttribute} from '@emm/core/models/item-attribute.model';
import {NotificationService} from '@emm/shared/services/notification.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {CreateVariation, Variation, VariationAssignment} from '@emm/common-items/models/variation.model';
import {ComponentDetails} from '@emm/common-items/models/component.model';
import {UserService} from '@emm/core/services/user.service';
import {
    AddComponentDialogComponent
} from '@emm/common-items/dialogs/add-component-dialog/add-component-dialog.component';
import {
    CreateOrEditAssignmentDialogComponent
} from '@emm/common-items/dialogs/create-or-edit-assignment-dialog/create-or-edit-assignment-dialog.component';
import {
    CreateVariationDialogComponent
} from '@emm/common-items/dialogs/create-variation-dialog/create-variation-dialog.component';
import {environment} from '@env';
import {formatDateToMidnight, MenuDateUtil} from '@emm/core/util/menu-date.util';
import {
    EditVariationMetadataDialogComponent
} from '@emm/common-items/dialogs/edit-variation-metadata-dialog/edit-variation-metadata-dialog.component';

@Component({
    selector: 'emm-common-item-details',
    templateUrl: './common-item-details.component.html',
    styleUrls: ['./common-item-details.component.scss']
})
export class CommonItemDetailsComponent implements OnInit, OnChanges, OnDestroy {

    protected readonly environment = environment;
    @Input() selectedItem: any;
    @Input() isReadOnly = false;
    @Input() useStoreData = false;
    @Input() context: {
        featureScope: string;
        location: string;
        submenu: string;
        consumer?: string;
    };
    baseVariation: Variation = {
        itemTag: 'BASE_ITEM',
        variationTag: 'BASE_ITEM',
        variationLabel: 'Base Item',
        variationDescription: 'Base Item',
        locationId: 'CHAIN',
        overrides: {}
    };
    variation$ = new BehaviorSubject<Variation>(this.baseVariation);
    variations$ = new BehaviorSubject<Variation[]>([]);
    assignments$ = new BehaviorSubject<VariationAssignment[]>([]);
    allAssignments$ = new BehaviorSubject<VariationAssignment[]>([]);
    assignmentsCache: VariationAssignment[];
    overridesLength$ = new BehaviorSubject<number>(0);
    hasComponentOverrides$ = new BehaviorSubject<boolean>(false);

    components$ = new BehaviorSubject<ComponentDetails[] | []>([]);
    modifiers: any[] = [];

    form: FormGroup;
    formReady = false;
    item$ = new BehaviorSubject<any>(null);
    modifierGroups: any[] = [];
    hideButtonsToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.HIDE_BUTTONS);
    showCommonItems$ = this.launchDarklyService.getBooleanFlag(FlagKeys.USE_COMMON_ITEMS);
    effectiveDate: Date | null = new Date(Date.now() + 24 * 60 * 60 * 1000);
    minDate: Date = new Date();
    locale: string | null = 'en-US';
    private destroy$ = new Subject<void>();
    isLoading = true;

    attributesByGroup: { [key: string]: ItemAttribute[] | any } = {};
    attributeGroups: string[] = [];
    isSuperAdmin = false;
    showAssignmentsWarning: boolean;
    showVariationEditIcon = false;
    user;
    private pendingVariationTag: string | null = null;

    constructor(
        private dialog: MatDialog,
        private itemDataService: ItemDataService,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private launchDarklyService: LaunchDarklyService,
        private itemDetailStore: ItemDetailStoreService,
        private notificationService: NotificationService,
        private router: Router,
        private userService: UserService,
    ) {}

    ngOnInit() {
        this.userService.getUser().pipe(
            takeUntil(this.destroy$),
            tap(user => {
                this.user = user;
                if (!user) {
                    this.notificationService.error('Failed to load user');
                }
            })
        ).subscribe();

        // Handle route changes for item group
        this.route.paramMap.pipe(
            skip(1),
            takeUntil(this.destroy$),
            map(params => params.get('id')),
            filter(id => !!id && this.item$.value?.tag !== id),
            tap(id => this.handleItemChange(id))
        ).subscribe();

        if (this.useStoreData) {
            this.isReadOnly = true;
            this.itemDetailStore.queries.selected$.pipe(
                takeUntil(this.destroy$),
                filter(item => !!item),
                tap(item => {
                    this.item$.next(item);
                    this.buildDynamicForm();
                    this.variations$.value.push(this.baseVariation);
                    this.changeVariation(this.baseVariation);
                })
            ).subscribe();
        } else {
            this.loadItem();
        }
        this.isSuperAdmin = this.userService.hasSuperAdminRole();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('selectedItem' in changes && !changes['selectedItem'].firstChange) {
            const currentItem = changes['selectedItem'].currentValue;
            const previousItem = changes['selectedItem'].previousValue;

            // Skip if the item is already loaded (avoids duplicate API calls)
            if (this.item$.value?.tag === currentItem?.tag) {
                return;
            }

            if (currentItem?.class === 'ITEM_GROUPING' && currentItem?.tag !== previousItem?.tag) {
                this.handleItemChange(currentItem.tag);
            }

            if (previousItem?.class === 'ITEM_GROUPING' && currentItem?.class && currentItem.class !== 'ITEM_GROUPING') {
                this.goToComponentDetail(currentItem);
            }
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleItemChange(newItemTag: string) {
        this.isLoading = true;
        this.resetComponentState();
        this.loadItemByTag(newItemTag);
    }

    resetComponentState() {
        this.formReady = false;
        this.variation$.next(this.baseVariation);
        this.variations$.next([]);
        this.assignments$.next([]);
        this.allAssignments$.next([]);
        this.assignmentsCache = [];
        this.overridesLength$.next(0);
        this.hasComponentOverrides$.next(false);
        this.components$.next([]);
        this.attributesByGroup = {};
        this.attributeGroups = [];
        this.showAssignmentsWarning = false;
    }

    private getItemServiceCall(tag: string, effectiveDate?: string): Observable<any> {
        if (this.isModifier) {
            return this.itemDataService.getModifier(tag, effectiveDate);
        } else if (this.isModifierGrouping) {
            return this.itemDataService.getModifierGroup(tag, effectiveDate);
        } else {
            return this.itemDataService.getItem(tag, effectiveDate || formatDateToMidnight(this.effectiveDate.toISOString()));
        }
    }

    loadItemByTag(tag: string) {
        const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());

        this.getItemServiceCall(tag, effectiveDate).pipe(
            tap((value) => {
                console.log(value);
                if (value) {
                    this.item$.next(value);
                    this.buildDynamicForm();
                } else {
                    this.notificationService.error('Item not found');
                    this.isLoading = false;
                }
            }),
            catchError(error => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                this.isLoading = false;
                return of(null);
            })
        ).subscribe();
    }

    loadItem() {
        const item = this.selectedItem;
        if (!item) {
            const id = this.route.snapshot.paramMap.get('id');
            this.getItemServiceCall(id).pipe(
                tap(value => {
                    console.log(value);
                    if (value) {
                        this.item$.next(value);
                    }
                    this.buildDynamicForm();
                })
            ).subscribe();
        } else if (item) {
            console.log(item);
            this.item$.next(item);
            this.buildDynamicForm();
        } else {
            this.attributesByGroup = {};
            this.attributeGroups = [];
        }
    }

    get itemClass(): string {
        const item = this.currentItem;
        return item?.class || '';
    }

    get itemObjectType(): string {
        const item = this.currentItem;
        return item?.objectType || '';
    }

    get isItem(): boolean {
        const isItemClass = this.itemClass === 'ITEM' || this.itemObjectType === 'ITEM';
        return isItemClass || this.router.url.includes('item-detail');
    }

    get isItemGrouping(): boolean {
        const isItemGroupClass = this.itemClass === 'ITEM_GROUPING' || this.itemObjectType === 'ITEM_GROUPING';
        return isItemGroupClass || this.router.url.includes('item-group-detail');
    }

    get isModifier(): boolean {
        const isModifierClass = this.itemClass === 'MODIFIER' || this.itemObjectType === 'MODIFIER';
        return isModifierClass || this.router.url.includes('modifier-detail');
    }

    get isModifierGrouping(): boolean {
        const isModifierGroupClass = this.itemClass === 'MODIFIER_GROUPING' || this.itemObjectType === 'MODIFIER_GROUPING';
        return isModifierGroupClass || this.router.url.includes('modifier-group-detail');
    }

    get itemClassLabel(): string {
        switch (this.itemClass) {
            case 'ITEM':
                return 'Item';
            case 'ITEM_GROUPING':
                return 'Item Group';
            case 'MODIFIER':
                return 'Modifier';
            case 'MODIFIER_GROUPING':
                return 'Modifier Group';
            default:
                return '';
        }
    }

    get componentCardLabel(): string {
        if (this.isItem) return 'Modifier Group';
        if (this.isItemGrouping) return 'Item';
        if (this.isModifierGrouping) return 'Modifier';
        return '';
    }

    getNewAttributeGroup(attribute: ItemAttribute): string {
        let keyAttributes: string[] = [];
        let groupName = 'Item';

        if (this.isItem) {
            keyAttributes = ['tag', 'name', 'description', 'retailModifiedItemId', 'subsGroupId'];
            groupName = 'Item';
        } else if (this.isItemGrouping) {
            keyAttributes = ['tag', 'name', 'description', 'defaultTag'];
            groupName = 'Item Group';
        } else if (this.isModifier) {
            keyAttributes = ['tag', 'name', 'description', 'retailModifiedItemId', 'subsGroupId', 'modifierType'];
            groupName = 'Modifier';
        } else if (this.isModifierGrouping) {
            keyAttributes = ['tag', 'name', 'description', 'modifierTypeGrouping', 'maximum'];
            groupName = 'Modifier Group';
        }

        const isKeyAttribute = keyAttributes.includes(attribute.jsonProperty);
        if (isKeyAttribute) {
            return groupName;
        } else {
            const groupMapping: { [key: string]: string } = {
                CORE: 'Others',
                PRICING: 'Pricing',
                IMAGE: 'Images',
                UI_VISIBILITY: 'Others',
            };
            const oldGroup = attribute.attributeGroup;
            return groupMapping[oldGroup] || 'Others';
        }
    }

    private sortAttributesByPriority(attrs: ItemAttribute[]): ItemAttribute[] {
        if (!Array.isArray(attrs) || !attrs.length) {
            return attrs;
        }

        const sortKey = (attr: ItemAttribute) => (attr?.name || attr?.jsonProperty || '').toString().toLowerCase();
        const sorted = [...attrs].sort((a, b) => {
            const aReq = !!a.required;
            const bReq = !!b.required;
            if (aReq !== bReq) {
                return aReq ? -1 : 1;
            }
            return sortKey(a).localeCompare(sortKey(b));
        });

        const priorityOrder = ['name'];
        const lower = (v: string) => v?.toLowerCase();
        const isPriority = (attr: ItemAttribute) => {
            const key = lower(attr.jsonProperty || attr.name);
            return priorityOrder.includes(key);
        };
        const priority = priorityOrder.flatMap(p => sorted.filter(a => lower(a.jsonProperty || a.name) === p));

        if (!priority.length) {
            return sorted;
        }

        const rest = sorted.filter(a => !isPriority(a));
        return [...priority, ...rest];
    }

    private elevateNameAndTag(attrs: ItemAttribute[]): ItemAttribute[] {
        return this.sortAttributesByPriority(attrs);
    }

    transformAttributes(item) {
        if (!item) {
            this.attributesByGroup = {};
            this.attributeGroups = [];
            return;
        }

        this.attributesByGroup = {};
        this.attributeGroups = [];
        const attrs = item.attributes;
        if (attrs && Array.isArray(attrs) && attrs.length > 0) {
            attrs.forEach((attr: ItemAttribute) => {
                if ((attr.jsonProperty === 'name' || attr.name?.toLowerCase() === 'name') && !this.isReadOnly) {
                    attr.required = true;
                }

                const newGroup = this.getNewAttributeGroup(attr);
                if (!this.attributesByGroup[newGroup]) {
                    this.attributesByGroup[newGroup] = [];
                    if (!this.attributeGroups.includes(newGroup)) {
                        this.attributeGroups.push(newGroup);
                    }
                }
                this.attributesByGroup[newGroup].push(attr);
            });

            Object.keys(this.attributesByGroup).forEach(group => {
                this.attributesByGroup[group] = this.sortAttributesByPriority(this.attributesByGroup[group]);
            });

            const groupOrder = [this.itemClassLabel, 'Pricing', 'Images', 'Others'];
            this.attributeGroups.sort((a, b) => {
                const indexA = groupOrder.indexOf(a);
                const indexB = groupOrder.indexOf(b);
                return indexA - indexB;
            });
        } else {
            // Handle items without attributes array or empty array
            const defaultGroup = this.itemClassLabel || 'Item';
            this.attributesByGroup[defaultGroup] = [];
            this.attributeGroups.push(defaultGroup);

            Object.keys(item).forEach(key => {
                if (this.shouldIncludeProperty(key)) {
                    const pseudoAttribute = {
                        jsonProperty: key,
                        value: item[key],
                        name: this.capitalizeWord(key),
                        required: key === 'name'
                    } as any;
                    this.attributesByGroup[defaultGroup].push(pseudoAttribute);
                }
            });
            this.attributesByGroup[defaultGroup] = this.sortAttributesByPriority(this.attributesByGroup[defaultGroup]);
        }
    }

    capitalizeWord(value: string): string {
        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    private deriveComponents(item: any, componentsSource?: any[]): any[] {
        const componentsValue = componentsSource ?? item.components ??
            item.attributes?.find(attr => attr.jsonProperty === 'components')?.value;
        const derivedComponents = (!componentsValue ||
            (Array.isArray(componentsValue) && componentsValue.length === 0)) &&
        item?.componentDetailsMap ? Object.keys(item.componentDetailsMap) : componentsValue;
        return derivedComponents || [];
    }

    private createFormControlsFromAttributes(attributes: ItemAttribute[]): { [key: string]: FormControl } {
        const formControls: { [key: string]: FormControl } = {};
        const usAttributes = attributes.filter(attr => attr.countryCode === 'US');
        const ordered = this.elevateNameAndTag(usAttributes);

        ordered.forEach((attr: ItemAttribute) => {
            const validators = [];
            if (attr.jsonProperty === 'name') {
                validators.push(Validators.required);
            } else if (attr.required && attr.type !== 'BOOL') {
                validators.push(Validators.required);
            }

            let value = attr.value;
            if (attr.jsonProperty === 'disabled') {
                value = value === true || value === 'true';
            }

            formControls[attr.jsonProperty] = new FormControl({
                value,
                disabled: this.isReadOnly
            }, validators);
        });

        return formControls;
    }

    private createFormControlsFromItem(item: any): { [key: string]: FormControl } {
        const formControls: { [key: string]: FormControl } = {};

        Object.keys(item).forEach(key => {
            if (this.shouldIncludeProperty(key)) {
                const validators = [];
                if (key === 'name') {
                    validators.push(Validators.required);
                }

                let value = (item as any)[key];
                if (key === 'disabled') {
                    value = value === true || value === 'true';
                }

                formControls[key] = new FormControl({
                    value,
                    disabled: this.isReadOnly
                }, validators);
            }
        });

        return formControls;
    }

    buildDynamicForm() {
        this.formReady = false;
        const item = this.item$.value;
        this.components$.next(this.deriveComponents(item));

        const sanitizedAttributes = (item?.attributes || []).filter(attr => {
            const key = attr.jsonProperty?.trim();
            return key !== 'components' && key !== 'componentDetailsMap';
        });

        this.buildCards(item);
        this.transformAttributes({...item, attributes: sanitizedAttributes});

        let formControls: { [key: string]: FormControl } = {};
        if (sanitizedAttributes.length) {
            formControls = this.createFormControlsFromAttributes(sanitizedAttributes);
        } else if (item) {
            formControls = this.createFormControlsFromItem(item);
        }

        this.form = this.fb.group(formControls);
        this.formReady = true;
        this.isLoading = false;
    }

    buildCards(item) {
        if (!this.isReadOnly) {
            const effectiveDate = MenuDateUtil.formatDateToMidnight(this.effectiveDate.toISOString());
            this.itemDataService.getItemVariations(item.tag, effectiveDate).pipe(
                tap((response: any) => {
                    response.unshift(this.baseVariation);
                    this.variations$.next(response);
                }),
                switchMap(() => this.itemDataService.getContextAssignments(item.tag, effectiveDate)),
                tap((response: any) => {
                    if (!this.useStoreData) {
                        this.assignmentsCache = response;
                        this.allAssignments$.next(response);
                        const currentVariationTag = this.variation$.value?.variationTag;
                        if (currentVariationTag && currentVariationTag !== 'BASE_ITEM') {
                            const filtered = response.filter(assignment => assignment.variationTag === currentVariationTag);
                            this.assignments$.next(filtered);
                        } else {
                            this.assignments$.next(response);
                        }
                    }
                    // After variations & assignments are loaded, restore previously selected variation if any
                    if (this.pendingVariationTag) {
                        const toSelect = this.variations$.value.find(v => v.variationTag === this.pendingVariationTag);
                        if (toSelect) {
                            this.changeVariation(toSelect);
                        }
                        this.pendingVariationTag = null;
                    }
                    this.shouldShowWarningBanner();
                }),
                catchError(error => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    return of(null);
                })
            ).subscribe();
        }
    }

    shouldIncludeProperty(propertyName: string): boolean {
        const excludedProperties = ['id', 'tag', 'class', 'type', 'migrated', 'components', 'componentDetails',
            'componentDetailsMap', 'attributes', 'createdDate', 'updatedDate', 'objectType'];
        return !excludedProperties.includes(propertyName);
    }

    getAttributesByGroup(group: string): ItemAttribute[] {
        return this.attributesByGroup[group] || [];
    }

    getFormControlType(attribute: ItemAttribute): string {
        if (attribute?.jsonProperty === 'disabled') {
            return 'checkbox';
        }

        switch (attribute?.type) {
            case 'TEXT':
                return 'input';
            case 'TEXTAREA':
                return 'textarea';
            case 'NUM':
                return 'number';
            case 'BOOL':
                return 'checkbox';
            case 'SELECT':
                return 'select';
            case 'MULTI_SELECT':
                return 'multi-select';
            case 'IMAGE':
                return 'input';
            default:
                return 'input';
        }
    }

    getAttributeOptions(attribute: ItemAttribute): any[] {
        if (!attribute?.options) {
            return [];
        }

        const options = attribute.options as any;

        if (Array.isArray(options)) {
            return options;
        }

        if (typeof options === 'string') {
            return options.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0);
        }

        if (typeof options === 'object') {
            return [options];
        }

        return [];
    }

    get currentItem() {
        return this.item$.value;
    }

    get itemName(): string {
        return this.currentItem?.name || '';
    }

    get itemTag(): string {
        return this.currentItem?.tag || '';
    }

    get itemTypeName(): string {
        return this.currentItem?.typeName || this.currentItem?.type || '';
    }

    get isCommonItem(): boolean {
        return this.currentItem?.commonItem || false;
    }

    get itemDescription(): string {
        const item = this.currentItem;
        if (!item?.attributes) return '';
        const descAttr = item.attributes.find((attr: ItemAttribute) => attr.jsonProperty === 'description');
        return descAttr?.value || '';
    }

    buildAttributesForUpdate(
        form,
        formValues = {},
        attributes = [],
    ) {
        const hasFormGet = form && typeof form.get === 'function';

        const isDirty = (property) => {
            if (!property) {
                return false;
            }
            if (hasFormGet) {
                const ctrl = form.get(property);
                return !!(ctrl && ctrl.dirty);
            }
            return false;
        };

        return attributes
            .filter(attr => attr.jsonProperty !== 'components')
            .filter(attr => isDirty(attr.jsonProperty))
            .map(attr => {
                const resolved = formValues?.[attr.jsonProperty] !== undefined
                    ? formValues[attr.jsonProperty]
                    : attr.value;

                const value = Array.isArray(resolved) ? JSON.stringify(resolved) : resolved;

                return {
                    ...attr,
                    value
                };
            });
    }


    save() {
        if (this.item$.value && !this.isReadOnly) {
            const currentVariation = this.variation$.value;

            if (currentVariation && currentVariation.variationLabel !== 'Base Item') {
                this.saveVariation();
            } else {
                this.saveBaseItem();
            }
        }
    }

    private isEffectiveDateToday(): boolean {
        if (!this.effectiveDate) {
            return false;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const compareDate = new Date(this.effectiveDate);
        compareDate.setHours(0, 0, 0, 0);
        return compareDate.getTime() === today.getTime();
    }

    private mergeAndDedupeComponents(existingRaw: any[], newRaw: string | any[]): string[] {
        const existing = (existingRaw || []).map(c => typeof c === 'string' ? c.trim() : c?.tag).filter(Boolean);
        let additions: string[] = [];
        if (Array.isArray(newRaw)) {
            additions = newRaw.map(c => typeof c === 'string' ? c.trim() : c?.tag).filter(Boolean);
        } else if (typeof newRaw === 'string') {
            additions = newRaw.split(',').map(c => c.trim()).filter(Boolean);
        }
        const existingSet = new Set<string>(existing);
        const newUnique = additions.filter(comp => !existingSet.has(comp));

        return [...existing, ...newUnique];
    }

    saveBaseItem() {
        if (this.isEffectiveDateToday()) {
            const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
                width: '400px',
                data: {
                    message: 'Warning: You are updating the base item with today\'s effective date.',
                    name: 'This change will take effect immediately. Do you want to proceed?',
                    endMessage: ''
                }
            });

            dialogRef.afterClosed().pipe(
                filter(confirmed => !!confirmed),
                tap(() => this.performBaseItemSave())
            ).subscribe();
        } else {
            this.performBaseItemSave();
        }
    }

    private performBaseItemSave() {
        const formValues = this.form.value;
        const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());
        const requestedBy = this.getRequestedByName();

        if (this.isModifier) {
            this.saveModifier(formValues, effectiveDate, requestedBy);
        } else if (this.isModifierGrouping) {
            this.saveModifierGroup(formValues, effectiveDate, requestedBy);
        } else {
            this.saveRegularItem(formValues, effectiveDate, requestedBy);
        }
    }

    private getRequestedByName(): string {
        return `${this.user?.firstName} ${this.user?.lastName}` ?? this.user?.login ?? 'unknown';
    }

    private saveModifier(formValues: any, effectiveDate: string, requestedBy: string): void {
        const modifierUpdates = this.buildModifierUpdates(formValues);
        this.cleanupUpdatePayload(modifierUpdates, true);

        this.itemDataService
            .updateModifier(this.item$.value.tag, modifierUpdates, effectiveDate, requestedBy)
            .pipe(
                tap(() => this.handleSuccessfulSave()),
                catchError(error => this.handleSaveError(error))
            )
            .subscribe();
    }

    private saveModifierGroup(formValues: any, effectiveDate: string, requestedBy: string): void {
        const updates = this.buildModifierGroupUpdates(formValues);
        this.cleanupUpdatePayload(updates, true);

        this.itemDataService
            .updateModifierGroup(this.item$.value.tag, updates, effectiveDate, requestedBy)
            .pipe(
                tap(() => this.handleSuccessfulSave()),
                catchError(error => this.handleSaveError(error))
            )
            .subscribe();
    }

    private saveRegularItem(formValues: any, effectiveDate: string, requestedBy: string): void {
        const itemValue = this.item$.value;
        delete itemValue['componentDetailsMap'];

        const updated: BaseItemRequestPayload = {
            ...itemValue,
            attributes: this.buildAttributesForUpdate(this.form, formValues, itemValue.attributes)
        };

        delete updated['migrated'];
        delete updated['class'];
        updated.attributes.forEach(attr => {
            if (attr['options']) {
                delete attr['options'];
            }
        });

        this.cleanupUpdatePayload(updated, false);

        this.itemDataService
            .updateItem(this.item$.value.tag, updated, effectiveDate, requestedBy)
            .pipe(
                tap(() => this.handleSuccessfulSave()),
                catchError(error => this.handleSaveError(error))
            )
            .subscribe();
    }

    private buildModifierUpdates(formValues: any): any {
        const updates: any = {};
        Object.keys(formValues).forEach(key => {
            const control = this.form.get(key);
            if (control && control.dirty) {
                this.applyFieldUpdate(updates, key, formValues[key], true);
            }
        });
        this.applyDefaults(updates, true);
        return updates;
    }

    private buildModifierGroupUpdates(formValues: any): any {
        const updates: any = {};
        Object.keys(formValues).forEach(key => {
            const control = this.form.get(key);
            if (control && control.dirty) {
                this.applyFieldUpdate(updates, key, formValues[key], false);
            }
        });
        this.applyDefaults(updates, false);
        return updates;
    }

    private applyFieldUpdate(updates: any, key: string, value: any, isModifier: boolean): void {
        const commonFields = {
            name: () => updates.name = value,
            description: () => updates.description = value,
            webImage: () => updates.image = value,
            image: () => updates.image = value,
            disabled: () => updates.disabled = this.parseBooleanValue(value),
            maximum: () => updates.maximum = this.parseIntValue(value),
        };

        const modifierOnlyFields = {
            tag: () => updates.tag = value,
            modifierType: () => updates.modifierType = value,
            type: () => updates.type = value,
            retailModifiedItemId: () => updates.rmi = this.parseIntValue(value),
            rmi: () => updates.rmi = this.parseIntValue(value),
            subsGroupId: () => updates.subsGroupId = this.parseIntValue(value),
            hideNutrition: () => updates.hideNutrition = this.parseBooleanValue(value),
            isLocked: () => updates.isLocked = this.parseBooleanValue(value),
        };

        const modifierGroupOnlyFields = {
            modifierTypeGrouping: () => updates.modifierTypeGrouping = value,
        };

        if (commonFields[key]) {
            commonFields[key]();
        } else if (isModifier && modifierOnlyFields[key]) {
            modifierOnlyFields[key]();
        } else if (!isModifier && modifierGroupOnlyFields[key]) {
            modifierGroupOnlyFields[key]();
        }
    }

    private applyDefaults(updates: any, isModifier: boolean): void {
        const commonDefaults = {
            image: 'image',
            name: 'name',
            description: 'description',
            type: 'type',
            maximum: 'maximum',
            disabled: 'disabled'
        };

        const modifierOnlyDefaults = {
            tag: 'tag',
            modifierType: 'modifierType',
            rmi: 'rmi',
            hideNutrition: 'hideNutrition',
            isLocked: 'isLocked',
            locationSetTag: 'locationSetTag',
            country: 'country'
        };

        const modifierGroupOnlyDefaults = {
            modifierTypeGrouping: 'modifierTypeGrouping',
        };

        const defaults = {
            ...commonDefaults,
            ...(isModifier ? modifierOnlyDefaults : modifierGroupOnlyDefaults)
        };

        Object.entries(defaults).forEach(([key, sourceKey]) => {
            if (updates[key] === undefined && this.item$.value?.[sourceKey] !== undefined) {
                updates[key] = this.item$.value[sourceKey];
            }
        });
    }

    private parseIntValue(value: any): number {
        return typeof value === 'string' ? parseInt(value, 10) : value;
    }

    private parseBooleanValue(value: any): boolean {
        if (value === 'True') return true;
        if (value === 'False') return false;
        return !!value;
    }

    private cleanupUpdatePayload(payload: any, deleteAttributes: boolean = false): void {
        const fieldsToDelete = ['components', 'createdDate', 'updatedDate', 'created', 'updated'];
        if (deleteAttributes) {
            fieldsToDelete.push('attributes');
        }
        fieldsToDelete.forEach(field => delete payload[field]);
    }

    private handleSuccessfulSave(): void {
        this.notificationService.success(`Successfully updated ${this.item$.value.tag}`);
        this.loadItemByTag(this.itemTag);
    }

    private handleSaveError(error: any): Observable<null> {
        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
        return of(null);
    }

    saveVariation() {
        if (this.isEffectiveDateToday()) {
            const currentVariation = this.variation$.value;
            const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
                width: '400px',
                data: {
                    message: `Warning: You are updating variation "${currentVariation.variationLabel}" with today's effective date.`,
                    name: 'This change will take effect immediately. Do you want to proceed?',
                    endMessage: ''
                }
            });

            dialogRef.afterClosed().pipe(
                filter(confirmed => !!confirmed),
                tap(() => this.performVariationSave())
            ).subscribe();
        } else {
            this.performVariationSave();
        }
    }

    private performVariationSave() {
        const currentVariation = this.variation$.value;
        const formValues = this.form.value;
        const nameControl = this.form.get('name');
        if (nameControl && nameControl.dirty && nameControl.enabled) {
            const nameValue = typeof nameControl.value === 'string' ? nameControl.value.trim() : nameControl.value;
            if (nameValue === '' || nameValue === null) {
                this.notificationService.error('Name cannot be empty');
                return;
            }
        }

        const overrides: any = {};
        Object.keys(formValues).forEach(key => {
            // Skip variationLabel and variationDescription - they should be at the top level, not in overrides
            if (key === 'variationLabel' || key === 'variationDescription') {
                return;
            }

            const control = this.form.get(key);
            if (control && control.dirty && control.enabled) {
                let value = formValues[key];

                if (typeof value === 'string') {
                    value = value.trim();
                    if (/^\d+$/.test(value)) {
                        const num = Number(value);
                        if (!isNaN(num)) value = num;
                    }
                }

                overrides[key] = value;
            }
        });

        if (this.hasComponentOverrides$.value && this.components$.value) {
            overrides.components = this.mergeAndDedupeComponents([], this.components$.value);
        }

        const mergedOverrides = {...currentVariation.overrides, ...overrides};
        if (currentVariation.overrides?.components || overrides.components) {
            mergedOverrides.components = this.mergeAndDedupeComponents(
                currentVariation.overrides?.components || [],
                overrides.components || []
            );
        }

        const overridesAnnotation = {note: 'Custom override applied'};

        const updates = {
            itemTag: currentVariation.itemTag,
            variationLabel: currentVariation.variationLabel,
            locationId: currentVariation.locationId,
            overrides: mergedOverrides,
            overridesAnnotation,
        };

        delete (updates as any).createdDate;
        delete (updates as any).updatedDate;
        delete (updates as any).created;
        delete (updates as any).updated;
        delete (updates as any).attributes;

        const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());
        const requestedBy = `${this.user?.firstName} ${this.user?.lastName}` ?? this.user?.login ?? 'unknown';
        this.itemDataService
            .updateItemVariation(currentVariation.variationTag, updates, effectiveDate, requestedBy)
            .pipe(
                tap(() => {
                    this.notificationService.success(`Successfully updated variation: ${currentVariation.variationLabel}`);
                    // Preserve the current variation selection so it's restored after reload
                    this.pendingVariationTag = currentVariation.variationTag;
                    this.loadItemByTag(this.itemTag);
                }),
                catchError(error => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    return of(null);
                })
            )
            .subscribe();
    }

    delete() {
        if (this.item$.value && !this.isReadOnly) {
            this.itemDataService.deleteItem(this.item$.value.id);
            this.item$.next(undefined);
            this.form.reset();
        }
    }

    previewImage(attribute): void {
        if (attribute.value) {
            this.dialog.open(PreviewImageDialogComponent, {
                data: {imageUrl: attribute.value},
                width: '500px',
                maxWidth: '90vw',
                panelClass: 'modifier-image-dialog-panel'
            });
        } else {
            this.notificationService.error('No image URL was found');
        }
    }

    getDisplayImage(): string {
        const item = this.currentItem;
        if (!item) return null;

        if (this.isModifier || this.isModifierGrouping) {
            return item?.image;
        }

        // For items and item groups
        if (!item?.attributes) return null;
        const mobileImageAttr = item.attributes.find((attr: ItemAttribute) => attr.jsonProperty === 'mobileImage');
        const webImageAttr = item.attributes.find((attr: ItemAttribute) => attr.jsonProperty === 'webImage');
        return mobileImageAttr?.value || webImageAttr?.value || null;
    }

    changeVariation(variation: Variation) {
        const filteredVariation = {
            ...variation,
            overrides: Object.keys(variation.overrides || {})
                .filter(key => key !== 'components')
                .reduce((filtered, key) => {
                    filtered[key] = variation.overrides[key];
                    return filtered;
                }, {})
        };
        const overridesLength = Object.keys(filteredVariation.overrides).length;
        this.overridesLength$.next(overridesLength);
        this.variation$.next(filteredVariation);
        this.hasComponentOverrides$.next(variation.overrides?.hasOwnProperty('components') ?? false);
        const currentVariation: Variation = this.variations$.value.find(variant => variant.variationTag === variation.variationTag);
        if (this.variation$.value.variationLabel !== 'Base Item') {
            this.form.disable();
            const overrides = currentVariation?.overrides || {};
            Object.entries(overrides).forEach(([key, value]) => {
                if (key !== 'components') {
                    if (this.form.controls[key]) {
                        if (!this.isReadOnly) {
                            this.form.controls[key].enable();
                        }
                        this.form.controls[key].setValue(value);
                    }
                }
            });
            if (this.useStoreData) {
                if (currentVariation.overrides?.hasOwnProperty('components')) {
                    this.components$.next(currentVariation.overrides.components || []);
                } else {
                    const attrComponents = this.item$.value.attributes?.find(attr => attr.jsonProperty === 'components')?.value;
                    const fallback = (!attrComponents || (Array.isArray(attrComponents) && attrComponents.length === 0)) &&
                    this.item$.value?.componentDetailsMap ? Object.keys(this.item$.value.componentDetailsMap) : attrComponents;
                    this.components$.next(fallback || []);
                }
            } else {
                if (currentVariation.overrides?.hasOwnProperty('components')) {
                    this.components$.next(currentVariation.overrides.components || []);
                } else {
                    const variationComponents = this.item$.value.components;
                    const fallback = (!variationComponents || (Array.isArray(variationComponents) &&
                        variationComponents.length === 0)) &&
                    this.item$.value?.componentDetailsMap ? Object.keys(this.item$.value.componentDetailsMap) : variationComponents;
                    this.components$.next(fallback || []);
                }
            }
            this.assignments$.next(this.assignmentsCache?.filter(assignment => assignment.variationTag === variation.variationTag));
        } else {
            this.resetToBaseItem();
            console.log(this.variation$.value);
        }
    }

    resetToBaseItem() {
        const item = this.item$.value;
        this.transformAttributes(item);

        let formControls: { [key: string]: FormControl } = {};
        if (item?.attributes) {
            const attributes = this.useStoreData ? item?.attributes :
                item?.attributes?.filter(attr => attr.countryCode === 'US');
            const ordered = this.elevateNameAndTag(attributes);
            ordered?.forEach((attr: ItemAttribute) => {
                const validators = [];
                if (attr.jsonProperty === 'name') {
                    validators.push(Validators.required);
                } else if (attr.required && attr.type !== 'BOOL') {
                    validators.push(Validators.required);
                }

                formControls[attr.jsonProperty] = new FormControl({
                    value: attr.value,
                    disabled: this.isReadOnly
                }, validators);
            });
        } else if (item) {
            formControls = this.createFormControlsFromItem(item);
        }

        this.form = this.fb.group(formControls);
        if (this.isReadOnly) {
            this.form.disable({emitEvent: false});
        } else {
            this.form.enable();
        }

        const baseComponents = this.useStoreData ?
            this.item$.value.attributes?.find(attr => attr.jsonProperty === 'components')?.value :
            this.item$.value.components;
        this.components$.next(this.deriveComponents(item, baseComponents));
        this.assignments$.next(this.assignmentsCache);
    }

    copyTagToClipboard(): void {
        navigator.clipboard.writeText(this.itemTag).then(() => {
            this.notificationService.success(`${this.itemTag} copied to clipboard`);
        }).catch(err => {
            this.notificationService.error(`Failed to copy tag: ${err}`);
        });
    }

    getUnassignedChainVariationTags(): Observable<string[]> {
        return combineLatest([this.variations$, this.allAssignments$]).pipe(
            map(([itemVariations, variationAssignments]) => {
                const assigned = new Set<string>(variationAssignments.map(assignment => assignment.variationTag));
                return itemVariations
                    .filter(variation =>
                        variation.locationId === 'CHAIN' &&
                        variation.variationTag !== 'BASE_ITEM' &&
                        !assigned.has(variation.variationTag)
                    )
                    .map(variation => variation.variationTag);
            })
        );
    }

    shouldShowWarningBanner(): void {
        this.getUnassignedChainVariationTags().pipe(
            tap(tags => {
                this.showAssignmentsWarning = tags.length === this.variations$.value?.length;
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    goToComponentDetail(componentTagOrItem: string | any) {
        let component;
        if (typeof (componentTagOrItem) === 'string') {
            component = this.item$.value['componentDetailsMap'][componentTagOrItem];
        } else {
            component = componentTagOrItem;
        }

        const routeMapping = {
            ITEM: ['/common-items/item-detail', component.tag],
            ITEM_GROUPING: ['/common-items/item-group-detail', component.tag],
            MODIFIER_GROUPING: ['/common-items/modifier-group-detail', component.tag],
            MODIFIER: ['/common-items/modifier-detail', component.tag]
        };

        const route = routeMapping[component.class];
        if (route) {
            this.router.navigate(route);
        }
    }

    openCreateVariationDialog() {
        this.dialog.open(CreateVariationDialogComponent, {
            width: '750px',
            height: '550px',
            data: {
                item: this.item$.value,
                existingVariations: this.variations$.value,
            }
        }).afterClosed().pipe(
            filter(result => !!result),
            map((result: CreateVariation) => {
                if (typeof result?.overrides?.components === 'string') {
                    const merged = this.mergeAndDedupeComponents(this.components$.value, result.overrides.components);
                    return {
                        ...result,
                        overrides: {
                            ...result.overrides,
                            components: merged,
                        }
                    };
                }
                return result;
            }),
            switchMap((result: CreateVariation) => {
                const requestedBy = `${this.user?.firstName} ${this.user?.lastName}` ?? this.user?.login ?? 'unknown';
                return this.itemDataService.createItemVariation(result, requestedBy).pipe(
                    tap(() => {
                        this.notificationService.success(`Successfully created variation: ${result.variationLabel}`);
                        this.loadItemByTag(this.itemTag);
                    }),
                    catchError(error => {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                        return of(null);
                    })
                );
            })
        ).subscribe();
    }

    openEditVariationDialog() {
        const currentVariation = this.variation$.value;
        this.dialog.open(EditVariationMetadataDialogComponent, {
            width: '500px',
            data: {
                variation: currentVariation
            }
        }).afterClosed().pipe().subscribe((result) => {
            if (!result) {
                return;
            }
            const updates = {
                itemTag: currentVariation.itemTag,
                variationLabel: result.variationLabel,
                variationDescription: result.variationDescription,
                locationId: currentVariation.locationId,
                overrides: currentVariation.overrides || {},
            };

            const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());
            const requestedBy = `${this.user?.firstName} ${this.user?.lastName}` ?? this.user?.login ?? 'unknown';

            this.itemDataService
                .updateItemVariation(currentVariation.variationTag, updates, effectiveDate, requestedBy).pipe(
                tap(() => {
                    this.notificationService.success(`Successfully updated variation: ${result.variationLabel}`);
                    this.pendingVariationTag = currentVariation.variationTag;
                    this.loadItemByTag(this.itemTag);
                }),
                catchError(error => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    return of(null);
                })
            )
                .subscribe();
        });
    }

    hasVariationMetadata(): boolean {
        const variation = this.variation$.value;
        if (!variation || variation.variationLabel === 'Base Item') {
            return true;
        }
        const hasLabel = variation.variationLabel && variation.variationLabel.trim() !== '';
        const hasDescription = variation.variationDescription && variation.variationDescription.trim() !== '';
        return hasLabel || hasDescription;
    }

    openAddComponentDialog() {
        const currentVariation = this.variation$.value;
        const isViewingVariation = currentVariation && currentVariation.variationTag !== 'BASE_ITEM';

        this.dialog.open(AddComponentDialogComponent, {
            width: '450px',
            height: '300px',
            data: {
                existingComponents: this.components$.value
            }
        }).afterClosed().pipe(
            filter(value => !!value),
            switchMap(value => {
                const merged = this.mergeAndDedupeComponents(this.components$.value, value || '');
                const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());
                const requestedBy = `${this.user?.firstName} ${this.user?.lastName}` ?? this.user?.login ?? 'unknown';

                return isViewingVariation
                    ? this.updateVariation(currentVariation, merged, effectiveDate, requestedBy)
                    : this.addComponents(merged, effectiveDate, requestedBy);
            })
        ).subscribe();
    }

    private updateVariation(currentVariation: any, merged: string[], effectiveDate: string, requestedBy: string) {
        const updates = {
            variationLabel: currentVariation.variationLabel,
            itemTag: currentVariation.itemTag,
            locationId: currentVariation.locationId,
            overrides: {
                ...currentVariation.overrides,
                components: merged
            }
        };

        return this.itemDataService.updateItemVariation(
            currentVariation.variationTag,
            updates,
            effectiveDate,
            requestedBy
        ).pipe(
            tap(this.handleSuccess('Components added to variation successfully', currentVariation.variationTag)),
            catchError(this.handleError)
        );
    }

    private addComponents(merged: string[], effectiveDate: string, requestedBy: string) {
        return this.itemDataService.addItemComponents(
            this.itemTag,
            merged,
            effectiveDate,
            requestedBy
        ).pipe(
            tap(this.handleSuccess('Components added successfully')),
            catchError(this.handleError)
        );
    }

    private handleSuccess(message: string, variationTag?: string) {
        return () => {
            this.notificationService.success(message);

            if (variationTag) {
                this.pendingVariationTag = variationTag;
            }

            this.loadItemByTag(this.itemTag);
        };
    }

    private handleError = (error: any) => {
        const message = error?.error?.error?.message || API_ERRORS.GenericErrorMessage;
        this.notificationService.error(message);
        return of(null);
    };

    private buildAssignmentFromDialogValue(value: any, currentVariation: Variation, hash?: string): any {
        const consumer = value.consumer;
        const location = value.location;
        const properties: any = {};

        Object.keys(value).forEach(key => {
            if (key !== 'consumer' && key !== 'location' && value[key]) {
                properties[key] = value[key];
            }
        });

        const assignment: any = {
            locationId: location,
            consumer,
            variationTag: currentVariation.variationTag,
            itemTag: this.itemTag,
            properties
        };

        if (hash) {
            assignment.hash = hash;
        }

        return assignment;
    }

    private updateAssignments(newOrUpdatedAssignment: any): Observable<any> {
        const requestedBy = `${this.user?.firstName} ${this.user?.lastName}` ?? this.user?.login ?? 'unknown';
        const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());
        newOrUpdatedAssignment = { ...newOrUpdatedAssignment, assign: true, };
        const payload = {
            requestedBy,
            assignments: [newOrUpdatedAssignment],
        };

        return this.itemDataService.updateContextAssignment(effectiveDate, payload);
    }

    openCreateAssignmentDialog() {
        const currentVariation = this.variation$.value;
        this.dialog.open(CreateOrEditAssignmentDialogComponent, {
            width: '750px',
            height: '750px',
            data: {
                locationId: currentVariation?.locationId,
                mode: 'create'
            }
        }).afterClosed().pipe(
            filter(value => !!value),
            switchMap((value) => {
                const newAssignment = this.buildAssignmentFromDialogValue(value, currentVariation);
                return this.updateAssignments(newAssignment).pipe(
                    tap(() => {
                        this.notificationService.success('Successfully created assignment');
                        this.pendingVariationTag = currentVariation.variationTag;
                        this.loadItemByTag(this.itemTag);
                    }),
                    catchError(error => {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                        return of(null);
                    })
                );
            })
        ).subscribe();
    }

    openEditAssignmentDialog(assignment: VariationAssignment) {
        const currentVariation = this.variation$.value;
        this.dialog.open(CreateOrEditAssignmentDialogComponent, {
            width: '750px',
            height: '750px',
            data: {
                locationId: currentVariation?.locationId,
                mode: 'edit',
                assignment
            }
        }).afterClosed().pipe(
            filter(value => !!value),
            switchMap((value) => {
                const updatedAssignment = this.buildAssignmentFromDialogValue(value, currentVariation, assignment.hash);
                return this.updateAssignments(updatedAssignment).pipe(
                    tap(() => {
                        this.notificationService.success('Successfully updated assignment');
                        this.pendingVariationTag = currentVariation.variationTag;
                        this.loadItemByTag(this.itemTag);
                    }),
                    catchError(error => {
                        this.notificationService.error(
                            error?.error?.error?.message || API_ERRORS.GenericErrorMessage
                        );
                        return of(null);
                    })
                );
            })
        ).subscribe();
    }

    get isVariationDisabled(): boolean {
        const variation = this.variation$.value;
        if (!variation || !variation.overrides) {
            return false;
        }
        const disabledValue = variation.overrides['disabled'];
        return disabledValue === true || disabledValue === 'true';
    }

    get isBaseItemDisabled(): boolean {
        const variation = this.variation$.value;
        if (!variation || variation.variationLabel !== 'Base Item') {
            return false;
        }
        const item = this.currentItem;
        if (!item) return false;

        // Check attributes array first (for items with attributes)
        const disabledAttr = item.attributes?.find((attr: ItemAttribute) => attr.jsonProperty === 'disabled');
        let disabledValue = disabledAttr?.value;

        // If no attributes array, check direct property (for modifiers/modifier groups)
        if (disabledValue === undefined && item.hasOwnProperty('disabled')) {
            disabledValue = item.disabled;
        }

        return disabledValue === true || disabledValue === 'true';
    }

    onEffectiveDateChanged(newDate: Date | null) {
        if (!newDate) {
            return;
        }

        if (newDate < this.minDate) {
            newDate = this.minDate;
        }
        this.effectiveDate = newDate;

        // Preserve currently selected non-base variation so we can restore post reload
        const currentVariationTag = this.variation$.value?.variationTag;
        if (currentVariationTag && currentVariationTag !== 'BASE_ITEM') {
            this.pendingVariationTag = currentVariationTag;
        } else {
            this.pendingVariationTag = null;
        }

        if (this.itemTag) {
            this.resetComponentState();
            this.loadItemByTag(this.itemTag);
        }
    }

    onComponentsReordered(reorderedTags: string[]) {
        const currentVariation = this.variation$.value;
        const isViewingVariation = currentVariation && currentVariation.variationTag !== 'BASE_ITEM';
        const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());
        const requestedBy = `${this.user?.firstName} ${this.user?.lastName}` ?? this.user?.login ?? 'unknown';

        if (isViewingVariation) {
            this.updateVariation(currentVariation, reorderedTags, effectiveDate, requestedBy).subscribe();
        } else {
            this.addComponents(reorderedTags, effectiveDate, requestedBy).subscribe();
        }
    }
}
